import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { Link, Typography } from "@aviary";
import { AuthHeader, AuthHeaderWrapper, SolvvyChatIcon } from "@shared/components";
import { useBreakpoints, useSharedGlobalConfig } from "@shared/hooks";
import type { PractitionerAdditionalAttributesType } from "@shared/types/PractitionerAdditionalAttributesType";
import { SignUpCarouselOrFooter, PractitionerSignUp } from "@unauthenticated/shared/components";
import { SimplifySignUp } from "@unauthenticated/shared/components/SimplifySignUp/SimplifySignUp";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import type { PractitionerSignUpMutationData } from "@unauthenticated/shared/data/mutations/PractitionerSignUp.mutation";
import type { PractitionerType } from "@unauthenticated/shared/data/types";
import { l } from "@unauthenticated/shared/locales/i18n";
import { PageContent, Page } from "@unauthenticated/signupSignin/components";

interface Props {
  practitionerTypes: PractitionerType[];
  additionalAttributes?: PractitionerAdditionalAttributesType;
  csuid: string;
  pageVersion: "prac_by_default_with_sso" | "prac_by_default";
  newSSOErrors: string;
}

const PractitionerSignUpPage: FC<Props> = ({
  practitionerTypes,
  additionalAttributes,
  csuid,
  pageVersion,
  newSSOErrors,
}) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const { isFromWellevate, isFromEmersonEcologics } = useSharedGlobalConfig();
  const isFromWellevateOrEmerson = isFromWellevate || isFromEmersonEcologics;

  const handleOnCompleted = (data: PractitionerSignUpMutationData) => {
    if (data.auth.practitionerSignUp.redirectPath) {
      window.location.assign(data.auth.practitionerSignUp.redirectPath);
    }
  };

  const renderSignUp = () => {
    if (isFromWellevateOrEmerson) return renderPractitionerSignUp();

    return renderSimplifySignUp();
  };

  const renderSimplifySignUp = () => (
    <>
      <AuthHeaderWrapper includeBorder>
        <AuthHeader showFromWellevate showFromEmersonEcologics />
        {!tablet.lessThan && renderSignInLink()}
      </AuthHeaderWrapper>
      <PageContent>
        <SimplifySignUp
          csuid={csuid}
          onCompleted={handleOnCompleted}
          additionalAttributes={additionalAttributes}
          newSSOErrors={newSSOErrors}
        />
      </PageContent>
    </>
  );

  const renderPractitionerSignUp = () => (
    <PageContent>
      <AuthHeaderWrapper>
        <AuthHeader showFromWellevate showFromEmersonEcologics />
      </AuthHeaderWrapper>
      <PractitionerSignUp
        practitionerTypes={practitionerTypes}
        additionalAttributes={additionalAttributes}
        csuid={csuid}
        pageVersion={pageVersion}
        onCompleted={handleOnCompleted}
        newSSOErrors={newSSOErrors}
      />
    </PageContent>
  );

  const renderSignInLink = () => (
    <Typography isMarginless>
      {t(l.common.HaveAcount)}{" "}
      <Link isColor="primary" to={authRoutes.login}>
        {t(l.common.SignIn)}
      </Link>
    </Typography>
  );

  return (
    <Page data-e2e="sign-up-page">
      {renderSignUp()}
      <SignUpCarouselOrFooter isWithinViewport={!tablet.greaterThan} hideFooterSignIn />
      <SolvvyChatIcon />
    </Page>
  );
};

export { PractitionerSignUpPage };
